import { SyntheticEvent, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { useContextSite } from "../../../context/Context";
import { useAgendamentoPorId } from "../../../hooks/queries/agendamento/useAgendamentoPorId/useAgendamentoPorId";
import { useConsultarFatura } from "../../../hooks/queries/pagamento/useConsultarFatura/useConsultarFatura";
import { useConfirmarPagamentoAgendamento } from "../../../hooks/queries/agendamento/useConfirmarPagamentoAgendamento/useConfirmarPagamentoAgendamento";
import { useConfirmarReembolsoAgendamento } from "../../../hooks/queries/agendamento/useConfirmarReembolsoAgendamento/useConfirmarReembolsoAgendamento";
import { useRetornarStatusIniciadoAgendamento } from "../../../hooks/queries/agendamento/useRetornarStatusIniciadoAgendamento/useRetornarStatusIniciadoAgendamento";
import { useDownloadLaudo } from "../../../hooks/queries/agendamento/useDownloadLaudo/useDownloadLaudo";
import { StatusPagamentoEnum } from "../../../enums/statusPagamento";
import { IReembolsoConfirmarForm } from "../../../types/agendamento";
import { IModalReembolsoV2Props } from "../../Atoms/ButtonOptionsV2";

const AGENDAMENTO_QUERY_KEY = (uuid: string) => ["agendamento-por-id", uuid];

export const useFormScheduleDetailV2 = () => {
  const { setIsLoad } = useContextSite();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const uuidAgendamento = searchParams.get("id") || "";

  const [modalReembolso, setModalReembolso] = useState<IModalReembolsoV2Props>({
    isOPen: false,
  });

  const { data: agendamento } = useAgendamentoPorId(uuidAgendamento);
  const { data: faturas } = useConsultarFatura(uuidAgendamento);
  const fatura = faturas?.content?.find(
    (f) => f.status === StatusPagamentoEnum.pending,
  );

  const { mutate: confirmarPagamentoMutate } =
    useConfirmarPagamentoAgendamento();
  const { mutate: confirmarReembolsoMutate } =
    useConfirmarReembolsoAgendamento();
  const { mutate: retornarStatusIniciadoMutate } =
    useRetornarStatusIniciadoAgendamento();
  const { mutate: downloadLaudoMutate } = useDownloadLaudo();

  function updateAgendamentoCache(data: unknown) {
    queryClient.setQueryData(
      AGENDAMENTO_QUERY_KEY(uuidAgendamento),
      (old: any) => ({ ...old, data }),
    );
  }

  function confirmarPagamento() {
    setIsLoad(true);
    confirmarPagamentoMutate(
      { uuid: uuidAgendamento },
      {
        onSuccess: ({ data }) => updateAgendamentoCache(data),
        onSettled: () => setIsLoad(false),
      },
    );
  }

  function retornarStatusIniciado() {
    setIsLoad(true);
    retornarStatusIniciadoMutate(
      { uuid: uuidAgendamento },
      {
        onSuccess: ({ data }) => updateAgendamentoCache(data),
        onSettled: () => setIsLoad(false),
      },
    );
  }

  function confirmarReembolso(e: SyntheticEvent) {
    e.preventDefault();
    setIsLoad(true);

    const payload: IReembolsoConfirmarForm = {
      uuid: uuidAgendamento,
      dataDevolucao: dayjs(modalReembolso?.date).toISOString(),
    };

    confirmarReembolsoMutate(payload, {
      onSuccess: ({ data }) => {
        updateAgendamentoCache(data);
        toast.success("Reembolso confirmado");
      },
      onSettled: () => {
        setIsLoad(false);
        setModalReembolso({ isOPen: false, date: null });
      },
    });
  }

  function downloadLaudo() {
    if (!agendamento?.uuid) return;
    setIsLoad(true);
    downloadLaudoMutate(
      { uuid: agendamento.uuid },
      { onSettled: () => setIsLoad(false) },
    );
  }

  function acessarFatura() {
    fatura?.pix?.qrcodeText !== null
      ? navigate(`/agendamento/${uuidAgendamento}/fatura`)
      : window.open(`${fatura?.url}.pdf`, "_blank");
  }

  function onFinalize() {
    queryClient.invalidateQueries({
      queryKey: AGENDAMENTO_QUERY_KEY(uuidAgendamento),
    });
  }

  return {
    agendamento,
    fatura,
    modalReembolso,
    setModalReembolso,
    confirmarPagamento,
    retornarStatusIniciado,
    confirmarReembolso,
    downloadLaudo,
    acessarFatura,
    onFinalize,
  };
};
